import { useTranslation } from 'react-i18next';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getXAxisDomainForHistoricGraph, minMaxAxisDomain } from '@/components/Charts/utils';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useMembershipType } from '@/hooks/useMembershipType';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';
import { formatFactKilogramsToTonnes, formatUnit } from '@/utils/formatting';
import { printYear } from '@/utils/formatting/date';

import { NoData } from '../../../components/NoData';
import { Tile, TileCapsuleContent, TileCta, TileTitle } from './Tile';

export const BGCarbonTile = () => {
  const { t } = useTranslation();
  const pathSet =
    useMembershipType() === MembershipWithOrganizationTypeEnum.land_steward ? paths.landSteward : paths.buyer;

  const plotGroupId = usePlotGroupId();

  const soilCarbonHistoricPerHaGraphFact = R1FactType.r1_soil_carbon_historic_per_ha_graph;

  const { [soilCarbonHistoricPerHaGraphFact]: factValue } = useOrganizationIndicatorGraphFact<
    CarbonHistoricGraphData[]
  >(soilCarbonHistoricPerHaGraphFact);

  const carbonHistoricPerHaGraphFormatted = factValue?.value
    ?.filter((d) => d.name === 'permanent_soil_carbon_per_ha')
    // @ts-ignore typescript does not allow arithmetic operations between date objects
    .sort((pointA, pointB) => new Date(pointA.date) - new Date(pointB.date))
    .map((d) => ({ ...d, date: new Date(d.date).getTime() }));

  const { [R1FactType.r1_carbon_bg_latest_analysis_per_ha]: carbonBgLatestAnalysis } =
    useOrganizationIndicatorReportingFact<number>(R1FactType.r1_carbon_bg_latest_analysis_per_ha);

  const carbonLatestAnalysisFormatted = formatFactKilogramsToTonnes(carbonBgLatestAnalysis);

  const chartData = carbonHistoricPerHaGraphFormatted;

  let carbonLatestAnalysisDisplay = `${useDisplayNumber(carbonLatestAnalysisFormatted?.value ?? 0)} ${formatUnit(
    carbonLatestAnalysisFormatted?.unit ?? UnitEnum['t/ha'],
  )}`;
  carbonLatestAnalysisDisplay = !isNullish(carbonLatestAnalysisFormatted?.value) ? carbonLatestAnalysisDisplay : '-';

  const noChartData = !chartData?.length;

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Tile
      chart={<Chart chartData={chartData} />}
      title={<TileTitle>{t('global.analysis.carbonStorageBg')}</TileTitle>}
      capsuleContent={<TileCapsuleContent>{carbonLatestAnalysisDisplay}</TileCapsuleContent>}
      cta={
        <TileCta to={buildPath(pathSet.soilDetails, { queryParams: { plotGroupId } })}>
          {t('global.analysis.viewDetails')}
        </TileCta>
      }
    />
  );
};

export const AGCarbonTile = () => {
  const facts = useAboveGroundCarbonFacts();

  if (isNullish(facts)) {
    return <NoData />;
  }

  return <AGCTile />;
};

const AGCTile = () => {
  const { t } = useTranslation();
  const pathSet =
    useMembershipType() === MembershipWithOrganizationTypeEnum.land_steward ? paths.landSteward : paths.buyer;

  const plotGroupId = usePlotGroupId();
  const facts = useAboveGroundCarbonFacts();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const aboveGroundCarbonHistoricPerHaGraphFact = facts!.perHa.graph;

  const { [aboveGroundCarbonHistoricPerHaGraphFact]: factValue } = useOrganizationIndicatorGraphFact<
    CarbonHistoricGraphData[]
  >(aboveGroundCarbonHistoricPerHaGraphFact);

  const carbonHistoricPerHaGraphFormatted = factValue?.value
    ?.filter((d) => d.name === 'above_ground_carbon_per_ha')
    // @ts-ignore typescript does not allow arithmetic operations between date objects
    .sort((pointA, pointB) => new Date(pointA.date) - new Date(pointB.date))
    .map((d) => ({ ...d, date: new Date(d.date).getTime() }));

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const carbonLatestAnalysisFact = facts!.perHa.latestAnalysis;
  const { [carbonLatestAnalysisFact]: carbonBgLatestAnalysis } =
    useOrganizationIndicatorReportingFact<number>(carbonLatestAnalysisFact);

  const carbonLatestAnalysisFormatted = formatFactKilogramsToTonnes(carbonBgLatestAnalysis);

  const chartData = carbonHistoricPerHaGraphFormatted;

  let carbonLatestAnalysisDisplay = `${useDisplayNumber(carbonLatestAnalysisFormatted?.value ?? 0)} ${formatUnit(
    carbonLatestAnalysisFormatted?.unit ?? UnitEnum['t/ha'],
  )}`;
  carbonLatestAnalysisDisplay = !isNullish(carbonLatestAnalysisFormatted?.value) ? carbonLatestAnalysisDisplay : '-';

  const noChartData = !chartData?.length;
  if (noChartData) {
    return <NoData />;
  }

  return (
    <Tile
      chart={<Chart chartData={chartData} />}
      title={<TileTitle>{t('global.analysis.carbonStorageAg')}</TileTitle>}
      capsuleContent={<TileCapsuleContent>{carbonLatestAnalysisDisplay}</TileCapsuleContent>}
      cta={
        <TileCta to={buildPath(pathSet.carbonDetails, { queryParams: { plotGroupId } })}>
          {t('global.analysis.viewDetails')}
        </TileCta>
      }
    />
  );
};

type CarbonHistoricGraphData = {
  date: number;
  name: 'permanent_soil_carbon_per_ha' | 'above_ground_carbon_per_ha';
  value: number;
  unit: 't';
};

const Chart = ({ chartData }: { chartData: CarbonHistoricGraphData[] }) => {
  const firstTick = chartData.at(0)?.date;
  const lastTick = chartData.at(-1)?.date;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick!, lastTick!) ?? [
    new Date().getFullYear(),
    new Date().getFullYear(),
  ];
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const startYear = xTicksDomain[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const endYear = xTicksDomain[1]!;
  const ticksRange = Array(endYear - startYear + 1).keys();
  const xTicks = Array.from(ticksRange).map((x) => new Date(`12-31-${startYear + x}`).getTime());
  const yearsSubset = xTicks.filter((_, i) => i % 2 !== 0 && i !== xTicks.length - 1);

  return (
    <ResponsiveContainer width='100%' height={80}>
      <AreaChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
      >
        <defs>
          <linearGradient id='bgCarbonHistorical' x1='0' y1='-2.6' x2='0' y2='1'>
            <stop offset='0%' stopColor='#FFDD5F' stopOpacity={1} />
            <stop offset='100%' stopColor='#FFDD5F' stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          isAnimationActive={false}
          type='monotone'
          dataKey='value'
          stroke='#E5BF2C'
          strokeWidth={2}
          fill='url(#bgCarbonHistorical)'
          fillOpacity={1}
        />
        <XAxis
          dataKey='date'
          scale='time'
          type='number'
          domain={['dataMin', 'dataMax']}
          ticks={yearsSubset}
          tickFormatter={printYear}
          axisLine={false}
          tickLine={false}
        />
        <YAxis hide dataKey='value' type='number' scale='sequential' width={0} domain={minMaxAxisDomain(0.8, 1.2)} />
      </AreaChart>
    </ResponsiveContainer>
  );
};
